import { useEffect, useState } from 'react'
import { Dialog } from '@headlessui/react'

import { Button } from 'components/Button'
import { Modal } from 'components/Modal'
import { commonStrings, termsManagementStrings } from 'constants/strings'

const strings = termsManagementStrings.editor

type PublishDialogProps = {
  open: boolean
  /** 公開中の版がない（初版） */
  isFirstPublication: boolean
  isPublishing: boolean
  onClose: () => void
  onConfirm: (reconsentRequired: boolean) => void
}

/**
 * 公開の確認。改定では再同意の要否を ADMIN が選び、影響範囲を表示する。
 * 誤操作で全員に再同意を求めないよう、既定は選択なし（どちらかを選ぶまで公開できない）。
 */
export function PublishDialog({
  open,
  isFirstPublication,
  isPublishing,
  onClose,
  onConfirm,
}: PublishDialogProps) {
  const [reconsent, setReconsent] = useState<'yes' | 'no' | undefined>()

  useEffect(() => {
    if (open) setReconsent(undefined)
  }, [open])

  const canPublish = isFirstPublication || reconsent !== undefined

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <Dialog.Title as="h2" className="mb-3 text-lg font-bold text-ink">
        {strings.publishTitle}
      </Dialog.Title>
      {isFirstPublication ? (
        <p className="mb-6 text-sm text-ink">{strings.publishFirstNotice}</p>
      ) : (
        <fieldset className="mb-6 grid gap-3">
          <legend className="mb-2 text-sm font-semibold text-ink">
            {strings.reconsentQuestion}
          </legend>
          {(
            [
              ['yes', strings.reconsentYes, strings.reconsentYesNote],
              ['no', strings.reconsentNo, strings.reconsentNoNote],
            ] as const
          ).map(([value, label, note]) => (
            <label
              key={value}
              className="flex cursor-pointer items-start gap-3 rounded-2xl border border-border/70 px-4 py-3"
            >
              <input
                type="radio"
                name="reconsent"
                value={value}
                checked={reconsent === value}
                onChange={() => setReconsent(value)}
                className="mt-1"
              />
              <span className="grid gap-1">
                <span className="text-sm font-semibold text-ink">{label}</span>
                <span className="text-xs text-muted">{note}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onClose}
          disabled={isPublishing}
        >
          {commonStrings.cancel}
        </Button>
        <Button
          type="button"
          className="flex-1"
          disabled={!canPublish}
          loading={isPublishing}
          onClick={() => onConfirm(isFirstPublication || reconsent === 'yes')}
        >
          {strings.publishConfirm}
        </Button>
      </div>
    </Modal>
  )
}
